import StatCell from '../../../components/analysis/StatCell';
import { GraduationCapIcon, UserCircleIcon, UsersIcon, UsersThreeIcon } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';

export default function ComparativeTestPerformanceTable({
  selectedSubject,
  comparativeScore,
  comparativeAccuracy,
  userAccuracy,
  userScore,
  totalMarks,
}) {
  const [subjectComparativeScore, setSubjectComparativeScore] = useState(null);
  const [subjectComparativeAccuracy, setSubjectComparativeAccuracy] = useState(null);
  const [subjectUserAccuracy, setSubjectUserAccuracy] = useState(null);
  const [subjectUserScore, setSubjectUserScore] = useState(null);

  useEffect(() => {
    if (!selectedSubject) return;

    const subjectId = selectedSubject?.id?.toString();

    setSubjectComparativeScore(comparativeScore?.filter((item) => item?.subject_id?.toString() === subjectId));
    setSubjectComparativeAccuracy(comparativeAccuracy?.filter((item) => item?.subject_id?.toString() === subjectId));
    setSubjectUserAccuracy(userAccuracy?.filter((item) => item?.subject_id?.toString() === subjectId));
    setSubjectUserScore(userScore?.filter((item) => item?.subject_id?.toString() === subjectId));
  }, [selectedSubject]);

  if (
    !selectedSubject ||
    !subjectComparativeScore ||
    !subjectComparativeAccuracy ||
    !subjectUserAccuracy ||
    !subjectUserScore
  )
    return null;

  const subjectId = selectedSubject?.id?.toString();
  const total = totalMarks?.[subjectId];

  const renderStatCell = (color, top, bottom, showPercentage = false) =>
    top !== null && top !== undefined ? (
      <StatCell color={color} top={top} bottom={bottom} showPercentage={showPercentage} />
    ) : (
      <div className="flex items-center justify-center h-8 text-xs text-[var(--q3-neutral-default)]">No data</div>
    );

  const renderComparativeRow = (icon, label, key) => (
    <tr className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
      <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
        <div className="flex items-center px-4 gap-4">
          {icon}
          <p className="truncate text-base font-semibold text-[var(--q3-neutral-default)]">{label}</p>
        </div>
      </td>
      <td className="border-r border-[var(--q3-stroke-light)]">
        {renderStatCell('var(--q3-accent-normal)', subjectComparativeScore?.[0]?.values?.[key], total)}
      </td>
      <td>{renderStatCell('var(--q3-green-normal)', subjectComparativeAccuracy?.[0]?.values?.[key], 100, true)}</td>
    </tr>
  );

  return (
    <div className="overflow-x-auto rounded-b-xl border border-t-0 border-[var(--q3-stroke-light)]">
      <table className="text-sm w-full">
        <thead className="bg-[var(--q3-surface-dim)]">
          <tr className="h-16">
            <td className="py-3 px-4 w-[318px] text-start border-r border-[var(--q3-stroke-light)]">
              <div className="line-clamp-2 max-w-[300px] leading-snug">Students</div>
            </td>
            {['Score', 'Accuracy'].map((label, i, arr) => (
              <td
                key={i}
                className={`py-3 px-4 min-w-[285px] text-center ${i < arr.length - 1 && 'border-r border-[var(--q3-stroke-light)]'}`}
              >
                <div className="line-clamp-2 max-w-[285px] mx-auto leading-snug">{label}</div>
              </td>
            ))}
          </tr>
        </thead>
        <tbody>
          {/* User row */}
          <tr className="border-b border-[var(--q3-stroke-light)] hover:bg-[var(--q3-surface-dim)]">
            <td className="h-16 w-75 border-r border-[var(--q3-stroke-light)]">
              <div className="flex items-center px-4 gap-4">
                <UserCircleIcon size={24} className="text-[var(--q3-neutral-default)]" />
                <p className="truncate text-base font-semibold text-[var(--q3-neutral-default)]">You</p>
              </div>
            </td>
            <td className="border-r border-[var(--q3-stroke-light)]">
              {renderStatCell('var(--q3-accent-normal)', subjectUserScore?.[0]?.score, total)}
            </td>
            <td>{renderStatCell('var(--q3-green-normal)', subjectUserAccuracy?.[0]?.accuracy, 100, true)}</td>
          </tr>

          {/* Comparative rows */}
          {renderComparativeRow(
            <GraduationCapIcon size={24} className="text-[var(--q3-neutral-default)]" />,
            'Topper',
            'topper',
          )}
          {renderComparativeRow(
            <UsersIcon size={24} className="text-[var(--q3-neutral-default)]" />,
            'Top 10%ile',
            'top_10_percent',
          )}
          {renderComparativeRow(
            <UsersThreeIcon size={24} className="text-[var(--q3-neutral-default)]" />,
            'Top 25%ile',
            'top_25_percent',
          )}
        </tbody>
      </table>
    </div>
  );
}
